/**
 * Every country a phone number can be issued in, with the calling code it is
 * reached on and the trunk digit it is dialled with at home.
 *
 * The whole ITU list, not the countries whose currency the rail quotes: where
 * a buyer's money comes from says nothing about where their SIM does, and a
 * shorter list makes a valid number unsendable.
 *
 * Generated from libphonenumber's metadata (v4, via libphonenumber-js 1.13.6)
 * and named by `Intl.DisplayNames`. Calling codes and trunk prefixes are ITU
 * and national-regulator facts; regenerate rather than hand-edit a row.
 */
export interface PhoneCountry {
  /** ISO 3166-1 alpha-2, which is also what the geo lookup answers. */
  alpha2: string
  /** E.164 calling code, `+` included. */
  dialCode: string
  /** English name, used when the platform can name no other. */
  name: string
  /**
   * The digit a national number is dialled with at home and never carries
   * internationally — `0` in most of Europe, `1` in the NANP, `8` in Russia.
   * Absent where the country has none: an Italian number keeps its zero.
   */
  trunkPrefix?: string
}

/** alpha-2, calling code, English name, trunk prefix where there is one. */
type PhoneCountryRow = readonly [string, string, string, string?]

const ROWS: readonly PhoneCountryRow[] = [
  ['AF', '+93', 'Afghanistan', '0'],
  ['AX', '+358', 'Åland Islands', '0'],
  ['AL', '+355', 'Albania', '0'],
  ['DZ', '+213', 'Algeria', '0'],
  ['AS', '+1', 'American Samoa', '1'],
  ['AD', '+376', 'Andorra'],
  ['AO', '+244', 'Angola'],
  ['AI', '+1', 'Anguilla', '1'],
  ['AG', '+1', 'Antigua & Barbuda', '1'],
  ['AR', '+54', 'Argentina', '0'],
  ['AM', '+374', 'Armenia', '0'],
  ['AW', '+297', 'Aruba'],
  ['AC', '+247', 'Ascension Island'],
  ['AU', '+61', 'Australia', '0'],
  ['AT', '+43', 'Austria', '0'],
  ['AZ', '+994', 'Azerbaijan', '0'],
  ['BS', '+1', 'Bahamas', '1'],
  ['BH', '+973', 'Bahrain'],
  ['BD', '+880', 'Bangladesh', '0'],
  ['BB', '+1', 'Barbados', '1'],
  ['BY', '+375', 'Belarus', '8'],
  ['BE', '+32', 'Belgium', '0'],
  ['BZ', '+501', 'Belize'],
  ['BJ', '+229', 'Benin'],
  ['BM', '+1', 'Bermuda', '1'],
  ['BT', '+975', 'Bhutan'],
  ['BO', '+591', 'Bolivia', '0'],
  ['BA', '+387', 'Bosnia & Herzegovina', '0'],
  ['BW', '+267', 'Botswana'],
  ['BR', '+55', 'Brazil', '0'],
  ['IO', '+246', 'British Indian Ocean Territory'],
  ['VG', '+1', 'British Virgin Islands', '1'],
  ['BN', '+673', 'Brunei'],
  ['BG', '+359', 'Bulgaria', '0'],
  ['BF', '+226', 'Burkina Faso'],
  ['BI', '+257', 'Burundi'],
  ['KH', '+855', 'Cambodia', '0'],
  ['CM', '+237', 'Cameroon'],
  ['CA', '+1', 'Canada', '1'],
  ['CV', '+238', 'Cape Verde'],
  ['BQ', '+599', 'Caribbean Netherlands'],
  ['KY', '+1', 'Cayman Islands', '1'],
  ['CF', '+236', 'Central African Republic'],
  ['TD', '+235', 'Chad'],
  ['CL', '+56', 'Chile'],
  ['CN', '+86', 'China', '0'],
  ['CX', '+61', 'Christmas Island', '0'],
  ['CC', '+61', 'Cocos (Keeling) Islands', '0'],
  ['CO', '+57', 'Colombia', '0'],
  ['KM', '+269', 'Comoros'],
  ['CG', '+242', 'Congo - Brazzaville'],
  ['CD', '+243', 'Congo - Kinshasa', '0'],
  ['CK', '+682', 'Cook Islands'],
  ['CR', '+506', 'Costa Rica'],
  ['CI', '+225', 'Côte d’Ivoire'],
  ['HR', '+385', 'Croatia', '0'],
  ['CU', '+53', 'Cuba', '0'],
  ['CW', '+599', 'Curaçao'],
  ['CY', '+357', 'Cyprus'],
  ['CZ', '+420', 'Czechia'],
  ['DK', '+45', 'Denmark'],
  ['DJ', '+253', 'Djibouti'],
  ['DM', '+1', 'Dominica', '1'],
  ['DO', '+1', 'Dominican Republic', '1'],
  ['EC', '+593', 'Ecuador', '0'],
  ['EG', '+20', 'Egypt', '0'],
  ['SV', '+503', 'El Salvador'],
  ['GQ', '+240', 'Equatorial Guinea'],
  ['ER', '+291', 'Eritrea', '0'],
  ['EE', '+372', 'Estonia'],
  ['SZ', '+268', 'Eswatini'],
  ['ET', '+251', 'Ethiopia', '0'],
  ['FK', '+500', 'Falkland Islands'],
  ['FO', '+298', 'Faroe Islands'],
  ['FJ', '+679', 'Fiji'],
  ['FI', '+358', 'Finland', '0'],
  ['FR', '+33', 'France', '0'],
  ['GF', '+594', 'French Guiana', '0'],
  ['PF', '+689', 'French Polynesia'],
  ['GA', '+241', 'Gabon'],
  ['GM', '+220', 'Gambia'],
  ['GE', '+995', 'Georgia', '0'],
  ['DE', '+49', 'Germany', '0'],
  ['GH', '+233', 'Ghana', '0'],
  ['GI', '+350', 'Gibraltar'],
  ['GR', '+30', 'Greece'],
  ['GL', '+299', 'Greenland'],
  ['GD', '+1', 'Grenada', '1'],
  ['GP', '+590', 'Guadeloupe', '0'],
  ['GU', '+1', 'Guam', '1'],
  ['GT', '+502', 'Guatemala'],
  ['GG', '+44', 'Guernsey', '0'],
  ['GN', '+224', 'Guinea'],
  ['GW', '+245', 'Guinea-Bissau'],
  ['GY', '+592', 'Guyana'],
  ['HT', '+509', 'Haiti'],
  ['HN', '+504', 'Honduras'],
  ['HK', '+852', 'Hong Kong SAR China'],
  ['HU', '+36', 'Hungary', '06'],
  ['IS', '+354', 'Iceland'],
  ['IN', '+91', 'India', '0'],
  ['ID', '+62', 'Indonesia', '0'],
  ['IR', '+98', 'Iran', '0'],
  ['IQ', '+964', 'Iraq', '0'],
  ['IE', '+353', 'Ireland', '0'],
  ['IM', '+44', 'Isle of Man', '0'],
  ['IL', '+972', 'Israel', '0'],
  ['IT', '+39', 'Italy'],
  ['JM', '+1', 'Jamaica', '1'],
  ['JP', '+81', 'Japan', '0'],
  ['JE', '+44', 'Jersey', '0'],
  ['JO', '+962', 'Jordan', '0'],
  ['KZ', '+7', 'Kazakhstan', '8'],
  ['KE', '+254', 'Kenya', '0'],
  ['KI', '+686', 'Kiribati', '0'],
  ['XK', '+383', 'Kosovo', '0'],
  ['KW', '+965', 'Kuwait'],
  ['KG', '+996', 'Kyrgyzstan', '0'],
  ['LA', '+856', 'Laos', '0'],
  ['LV', '+371', 'Latvia'],
  ['LB', '+961', 'Lebanon', '0'],
  ['LS', '+266', 'Lesotho'],
  ['LR', '+231', 'Liberia', '0'],
  ['LY', '+218', 'Libya', '0'],
  ['LI', '+423', 'Liechtenstein', '0'],
  ['LT', '+370', 'Lithuania', '0'],
  ['LU', '+352', 'Luxembourg'],
  ['MO', '+853', 'Macao SAR China'],
  ['MG', '+261', 'Madagascar', '0'],
  ['MW', '+265', 'Malawi', '0'],
  ['MY', '+60', 'Malaysia', '0'],
  ['MV', '+960', 'Maldives'],
  ['ML', '+223', 'Mali'],
  ['MT', '+356', 'Malta'],
  ['MH', '+692', 'Marshall Islands', '1'],
  ['MQ', '+596', 'Martinique', '0'],
  ['MR', '+222', 'Mauritania'],
  ['MU', '+230', 'Mauritius'],
  ['YT', '+262', 'Mayotte', '0'],
  ['MX', '+52', 'Mexico'],
  ['FM', '+691', 'Micronesia'],
  ['MD', '+373', 'Moldova', '0'],
  ['MC', '+377', 'Monaco', '0'],
  ['MN', '+976', 'Mongolia', '0'],
  ['ME', '+382', 'Montenegro', '0'],
  ['MS', '+1', 'Montserrat', '1'],
  ['MA', '+212', 'Morocco', '0'],
  ['MZ', '+258', 'Mozambique'],
  ['MM', '+95', 'Myanmar (Burma)', '0'],
  ['NA', '+264', 'Namibia', '0'],
  ['NR', '+674', 'Nauru'],
  ['NP', '+977', 'Nepal', '0'],
  ['NL', '+31', 'Netherlands', '0'],
  ['NC', '+687', 'New Caledonia'],
  ['NZ', '+64', 'New Zealand', '0'],
  ['NI', '+505', 'Nicaragua'],
  ['NE', '+227', 'Niger'],
  ['NG', '+234', 'Nigeria', '0'],
  ['NU', '+683', 'Niue'],
  ['NF', '+672', 'Norfolk Island'],
  ['KP', '+850', 'North Korea', '0'],
  ['MK', '+389', 'North Macedonia', '0'],
  ['MP', '+1', 'Northern Mariana Islands', '1'],
  ['NO', '+47', 'Norway'],
  ['OM', '+968', 'Oman'],
  ['PK', '+92', 'Pakistan', '0'],
  ['PW', '+680', 'Palau'],
  ['PS', '+970', 'Palestinian Territories', '0'],
  ['PA', '+507', 'Panama'],
  ['PG', '+675', 'Papua New Guinea'],
  ['PY', '+595', 'Paraguay', '0'],
  ['PE', '+51', 'Peru', '0'],
  ['PH', '+63', 'Philippines', '0'],
  ['PL', '+48', 'Poland'],
  ['PT', '+351', 'Portugal'],
  ['PR', '+1', 'Puerto Rico', '1'],
  ['QA', '+974', 'Qatar'],
  ['RE', '+262', 'Réunion', '0'],
  ['RO', '+40', 'Romania', '0'],
  ['RU', '+7', 'Russia', '8'],
  ['RW', '+250', 'Rwanda', '0'],
  ['WS', '+685', 'Samoa'],
  ['SM', '+378', 'San Marino'],
  ['ST', '+239', 'São Tomé & Príncipe'],
  ['SA', '+966', 'Saudi Arabia', '0'],
  ['SN', '+221', 'Senegal'],
  ['RS', '+381', 'Serbia', '0'],
  ['SC', '+248', 'Seychelles'],
  ['SL', '+232', 'Sierra Leone', '0'],
  ['SG', '+65', 'Singapore'],
  ['SX', '+1', 'Sint Maarten', '1'],
  ['SK', '+421', 'Slovakia', '0'],
  ['SI', '+386', 'Slovenia', '0'],
  ['SB', '+677', 'Solomon Islands'],
  ['SO', '+252', 'Somalia', '0'],
  ['ZA', '+27', 'South Africa', '0'],
  ['KR', '+82', 'South Korea', '0'],
  ['SS', '+211', 'South Sudan', '0'],
  ['ES', '+34', 'Spain'],
  ['LK', '+94', 'Sri Lanka', '0'],
  ['BL', '+590', 'St. Barthélemy', '0'],
  ['SH', '+290', 'St. Helena'],
  ['KN', '+1', 'St. Kitts & Nevis', '1'],
  ['LC', '+1', 'St. Lucia', '1'],
  ['MF', '+590', 'St. Martin', '0'],
  ['PM', '+508', 'St. Pierre & Miquelon', '0'],
  ['VC', '+1', 'St. Vincent & Grenadines', '1'],
  ['SD', '+249', 'Sudan', '0'],
  ['SR', '+597', 'Suriname'],
  ['SJ', '+47', 'Svalbard & Jan Mayen'],
  ['SE', '+46', 'Sweden', '0'],
  ['CH', '+41', 'Switzerland', '0'],
  ['SY', '+963', 'Syria', '0'],
  ['TW', '+886', 'Taiwan', '0'],
  ['TJ', '+992', 'Tajikistan'],
  ['TZ', '+255', 'Tanzania', '0'],
  ['TH', '+66', 'Thailand', '0'],
  ['TL', '+670', 'Timor-Leste'],
  ['TG', '+228', 'Togo'],
  ['TK', '+690', 'Tokelau'],
  ['TO', '+676', 'Tonga'],
  ['TT', '+1', 'Trinidad & Tobago', '1'],
  ['TA', '+290', 'Tristan da Cunha'],
  ['TN', '+216', 'Tunisia'],
  ['TR', '+90', 'Türkiye', '0'],
  ['TM', '+993', 'Turkmenistan', '8'],
  ['TC', '+1', 'Turks & Caicos Islands', '1'],
  ['TV', '+688', 'Tuvalu'],
  ['VI', '+1', 'U.S. Virgin Islands', '1'],
  ['UG', '+256', 'Uganda', '0'],
  ['UA', '+380', 'Ukraine', '0'],
  ['AE', '+971', 'United Arab Emirates', '0'],
  ['GB', '+44', 'United Kingdom', '0'],
  ['US', '+1', 'United States', '1'],
  ['UY', '+598', 'Uruguay', '0'],
  ['UZ', '+998', 'Uzbekistan'],
  ['VU', '+678', 'Vanuatu'],
  ['VA', '+39', 'Vatican City'],
  ['VE', '+58', 'Venezuela', '0'],
  ['VN', '+84', 'Vietnam', '0'],
  ['WF', '+681', 'Wallis & Futuna'],
  ['EH', '+212', 'Western Sahara', '0'],
  ['YE', '+967', 'Yemen', '0'],
  ['ZM', '+260', 'Zambia', '0'],
  ['ZW', '+263', 'Zimbabwe', '0'],
]

/** Ordered by English name, which is the order the list draws them in. */
export const PHONE_COUNTRIES: readonly PhoneCountry[] = ROWS.map(
  ([alpha2, dialCode, name, trunkPrefix]) => ({
    alpha2,
    dialCode,
    name,
    ...(trunkPrefix === undefined ? {} : { trunkPrefix }),
  }),
)

/** Where the field starts when the geo lookup names nothing this list has. */
const FALLBACK_ALPHA2 = 'US'

export function phoneCountry(
  alpha2: string | undefined,
): PhoneCountry | undefined {
  if (alpha2 === undefined) {
    return undefined
  }
  const wanted = alpha2.toUpperCase()
  return PHONE_COUNTRIES.find((country) => country.alpha2 === wanted)
}

/** The country the field opens on, from wherever the buyer is. */
export function defaultPhoneCountry(alpha2: string | undefined): PhoneCountry {
  return phoneCountry(alpha2) ?? DEFAULT_COUNTRY
}

const DEFAULT_COUNTRY: PhoneCountry = PHONE_COUNTRIES.find(
  (country) => country.alpha2 === FALLBACK_ALPHA2,
) ?? { alpha2: FALLBACK_ALPHA2, dialCode: '+1', name: 'United States' }

/**
 * `\u{1F1E6}\u{1F1FA}` from `AU`: alpha-2 letters map onto the
 * regional-indicator block, so the flag needs no asset and no per-country
 * entry.
 */
export function countryFlag(alpha2: string): string {
  return [...alpha2.toUpperCase()]
    .map((letter) =>
      String.fromCodePoint(REGIONAL_INDICATOR_A + letter.charCodeAt(0) - 65),
    )
    .join('')
}

const REGIONAL_INDICATOR_A = 0x1f1e6

/**
 * The country's name in the app's language.
 *
 * Read off the platform rather than the translation files: 245 countries in 8
 * locales is copy nobody would review, and every runtime that ships ICU
 * already knows them. The table's English is the fallback where it does not.
 */
export function countryName(country: PhoneCountry, locale: string): string {
  return regionNames(locale)?.of(country.alpha2) ?? country.name
}

// One per locale: constructing a formatter is expensive and the list rebuilds
// it on every render otherwise.
const REGION_NAMES = new Map<string, Intl.DisplayNames | undefined>()

function regionNames(locale: string): Intl.DisplayNames | undefined {
  if (!REGION_NAMES.has(locale)) {
    REGION_NAMES.set(locale, buildRegionNames(locale))
  }
  return REGION_NAMES.get(locale)
}

function buildRegionNames(locale: string): Intl.DisplayNames | undefined {
  try {
    return new Intl.DisplayNames([locale], { type: 'region' })
  } catch {
    return undefined
  }
}

/**
 * The wire value: the code and the digits typed under it, with nothing
 * between. An unanswered field stays empty rather than becoming a bare code —
 * `+61` alone is not an incomplete number, it is no number.
 */
export function toE164(country: PhoneCountry, national: string): string {
  const digits = withoutTrunkPrefix(country, national.replace(/\D/g, ''))
  return digits.length === 0 ? '' : `${country.dialCode}${digits}`
}

/**
 * Drops the digit that only means anything inside the country.
 *
 * `0412 345 678` is how an Australian writes their own number, and sending it
 * as `+610412345678` is a number nobody answers. The prefix is per country —
 * Italy has none and keeps its zero — so the table is what decides, not a
 * rule applied everywhere.
 */
function withoutTrunkPrefix(country: PhoneCountry, digits: string): string {
  const { trunkPrefix } = country
  if (trunkPrefix === undefined || !digits.startsWith(trunkPrefix)) {
    return digits
  }
  return digits.slice(trunkPrefix.length)
}

/** Longest first, so a code that opens with a shorter one is read whole. */
const DIAL_CODES: readonly string[] = [
  ...new Set(PHONE_COUNTRIES.map((country) => country.dialCode)),
].sort((left, right) => right.length - left.length)

/** The code a number opens with, read off digits carrying no `+`. */
export function leadingDialCode(digits: string): string | undefined {
  return DIAL_CODES.find((code) => digits.startsWith(code.slice(1)))
}

/**
 * Which country a shared code is drawn as — libphonenumber's main country for
 * it, since table order would name Guernsey for `+44` and American Samoa for
 * `+1`. Generated from `country_calling_codes`, whose first entry is the main
 * country; regenerate rather than hand-edit a row.
 */
const MAIN_ON_SHARED_CODE: Readonly<Record<string, string>> = {
  '+1': 'US',
  '+7': 'RU',
  '+39': 'IT',
  '+44': 'GB',
  '+47': 'NO',
  '+61': 'AU',
  '+212': 'MA',
  '+262': 'RE',
  '+290': 'SH',
  '+358': 'FI',
  '+590': 'GP',
  '+599': 'CW',
}

/** The chip keeps a code it already carries; else the code's own country. */
export function countryOnDialCode(
  code: string,
  preferred: PhoneCountry,
): PhoneCountry {
  if (preferred.dialCode === code) {
    return preferred
  }
  return (
    phoneCountry(MAIN_ON_SHARED_CODE[code]) ??
    PHONE_COUNTRIES.find((country) => country.dialCode === code) ??
    preferred
  )
}

/**
 * What the box shows: the number less any code the table knows, not only the
 * chip's — autofill hands the contact over with its own.
 */
export function nationalPart(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (!value.trimStart().startsWith('+')) {
    return digits
  }
  const code = leadingDialCode(digits)
  return code === undefined ? digits : digits.slice(code.length - 1)
}
